import React, { createContext, useContext, useRef, useCallback, ReactNode } from 'react';

interface AnnouncementContextType {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  announceError: (message: string) => void;
  announceSuccess: (message: string) => void;
  announceLoading: (message: string) => void;
  announceComplete: (message: string) => void;
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);

export const useAnnouncement = () => {
  const context = useContext(AnnouncementContext);
  if (!context) {
    throw new Error('useAnnouncement must be used within an AnnouncementProvider');
  }
  return context;
};

interface AnnouncementProviderProps {
  children: ReactNode;
}

export const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({ children }) => {
  const politeRef = useRef<HTMLDivElement>(null);
  const assertiveRef = useRef<HTMLDivElement>(null);

  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const targetRef = priority === 'assertive' ? assertiveRef : politeRef;

    if (targetRef.current) {
      // Clear the region first to ensure screen readers announce repeated messages
      targetRef.current.textContent = '';

      // Use setTimeout to ensure the clear is processed before the new message
      setTimeout(() => {
        if (targetRef.current) {
          targetRef.current.textContent = message;
        }
      }, 100);
    }
  }, []);

  const announceError = useCallback((message: string) => {
    announce(`Error: ${message}`, 'assertive');
  }, [announce]);

  const announceSuccess = useCallback((message: string) => {
    announce(`Success: ${message}`, 'polite');
  }, [announce]);

  const announceLoading = useCallback((message: string) => {
    announce(`Loading: ${message}`, 'polite');
  }, [announce]);

  const announceComplete = useCallback((message: string) => {
    announce(`Complete: ${message}`, 'polite');
  }, [announce]);

  const value: AnnouncementContextType = {
    announce,
    announceError,
    announceSuccess,
    announceLoading,
    announceComplete,
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
      {/* Screen reader live regions - visually hidden but announced */}
      <div
        ref={politeRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
      <div
        ref={assertiveRef}
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
    </AnnouncementContext.Provider>
  );
};
